import React, { useCallback, useEffect, useState } from "react";

import { getAdminMethod, getApplications, updateMethod } from "services";

import { incomplete, useCancelAsyncOnUnmount, useParams, useTranslation } from "utils";

import { AdminHead, Button, Col, Drawer, ErrorBoundary, Form, Link, MainPage, Row, Tabs } from "components";

import { MethodContinuationTab } from "pages/admin/programs/components/MethodContinuationTab";
import { MethodGeneralTab } from "pages/admin/programs/components/MethodGeneralTab";
import { MethodPaTab } from "pages/admin/programs/components/MethodPaTab";
import { MethodPoTab } from "pages/admin/programs/components/MethodPoTab";
import { MethodPqTab } from "pages/admin/programs/components/MethodPqTab";
import { MethodRecommendationsTab } from "pages/admin/programs/components/MethodRecommendationsTab";
import { MethodTasksTab } from "pages/admin/programs/components/MethodTasksTab";

export const MethodUpdate = () => {
  const { t } = useTranslation("enums");

  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [imageSuccess, setImageSuccess] = useState(false);
  const [imageURL, setImageURL] = useState(false);
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [advantages, setAdvantages] = useState("");
  const [disadvantages, setDisadvantages] = useState("");
  const [co2Storage, setCo2Storage] = useState("");
  const [proceeds, setProceeds] = useState("");
  const [continuation, setContinuation] = useState(false);
  const [method, setMethod] = useState({});
  const [applications, setApplications] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [cropFactors, setCropFactors] = useState([
    {
      crop: undefined,
      factor: undefined,
    },
  ]);
  const [tasks, setTasks] = useState([
    {
      title: undefined,
      description: undefined,
      minDate: undefined,
      maxDate: undefined,
      question: undefined,
      questionDescription: undefined,
      questionValues: [],
      questionRequired: false,
      count: 1,
      taskIcon: undefined,
    },
  ]);
  const [ShowDrawer, setShowDrawer] = useState(false);
  const [sa, setSa] = useState(0);
  const [sq, setSq] = useState(0);
  const [postSuggestion1, setPostSuggestion1] = useState("");
  const [postSuggestion2, setPostSuggestion2] = useState("");
  const [quantityQuestionPlaceholder, setQuantityQuestionPlaceholder] = useState(false);
  const signal = useCancelAsyncOnUnmount()();

  const taskIcons = [
    "tabler/macro",
    "klim/field",
    "klim/planting-seeds",
    "klim/planting-plant",
    "klim/falling-leaves",
    "klim/blooming-buds",
    "klim/crops-emerging",
    "klim/crops-branching",
    "klim/crops-matured",
    "klim/tillage",
  ];

  const getMethod = useCallback(() => {
    setLoading(true);

    getAdminMethod({
      id: id,
      signal,
    })
      .then((res) => {
        setMethod(res);
        setQuestions(res.questions);
        setTasks(res.tasks);
        setImageSuccess(res.image !== "");
        setImageURL(res.image !== "" ? res.image : false);
        setContent(res.content);
        setDescription(res.description);
        setAdvantages(res.advantages);
        setDisadvantages(res.disadvantages);
        setCo2Storage(res.co2Storage);
        setProceeds(res.proceeds);
        setCropFactors(res.cropFactors ? res.cropFactors.concat(cropFactors) : cropFactors);
        setContinuation(res.continuation);
        setSa(res.sa);
        setSq(res.sq);
        setPostSuggestion1(res.postSuggestion1);
        setPostSuggestion2(res.postSuggestion2);
        setQuantityQuestionPlaceholder(res.quantityQuestionPlaceholder);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [cropFactors, id, signal]);

  const getAllApplications = useCallback(() => {
    getApplications({
      signal,
    }).then((res) => {
      setApplications(res);
    });
  }, [signal]);

  const toggleContinuation = (v) => {
    if (v) {
      setContinuation({
        frequency: undefined,
        cf: undefined,
        title: "",
        description: "",
      });
    } else {
      setContinuation(false);
    }
  };

  const updateContinuation = (item, v) => {
    continuation[item] = v;
    setContinuation(continuation);
  };

  const addQuestion = (section) => {
    setQuestions([
      ...questions,
      {
        question: undefined,
        placeholder: null,
        type: undefined,
        values: [],
        optional: undefined,
        sf: [],
        section: section,
        count: questions.length > 0 && questions[questions.length - 1] ? questions[questions.length - 1].count + 1 : 1,
      },
    ]);
  };

  const updateQuestion = (i, e) => {
    e.persist();
    questions[i].question = e.target.value;

    setQuestions([...questions]);
  };

  const updatePlaceholder = (i, e) => {
    e.persist();
    questions[i].placeholder = e.target.value;

    setQuestions([...questions]);
  };

  const updateQuestionType = (i, v) => {
    questions[i].type = v;

    setQuestions([...questions]);
  };

  const updateQuestionOptions = (i, v) => {
    questions[i].values = v;

    setQuestions([...questions]);
  };

  const updateQuestionOptional = (i, v) => {
    questions[i].optional = v;

    setQuestions([...questions]);
  };

  const updateQuestionSf = (i, k, e) => {
    e.persist();
    questions[i].sf[k] = e.target.value;

    setQuestions([...questions]);
  };

  const moveQuestionUp = (i) => {
    let oldIndex = i;
    let newIndex = i - 1;
    let k = 0;

    while (oldIndex < 0) {
      oldIndex += questions.length;
    }

    while (newIndex < 0) {
      newIndex += questions.length;
    }

    if (newIndex >= questions.length) {
      k = newIndex - questions.length + 1;
      // eslint-disable-next-line
      while (k--) {
        questions.push(undefined);
      }
    }

    questions.splice(newIndex, 0, questions.splice(oldIndex, 1)[0]);
    questions.unshift(undefined);

    let count = 1;
    questions.forEach((item, i) => {
      if (item) {
        questions[i].count = count;
        count += 1;
      }
    });

    for (let i = 0; i < count; i += 1) {
      questions.unshift(undefined); // hack required to force re-render of form items
    }

    setQuestions([...questions]);
  };

  const moveQuestionDown = (i) => {
    let oldIndex = i;
    let newIndex = i + 1;
    let k = 0;

    while (oldIndex < 0) {
      oldIndex += questions.length;
    }

    while (newIndex < 0) {
      newIndex += questions.length;
    }

    if (newIndex >= questions.length) {
      k = newIndex - questions.length + 1;
      // eslint-disable-next-line
      while (k--) {
        questions.push(undefined);
      }
    }

    questions.splice(newIndex, 0, questions.splice(oldIndex, 1)[0]);

    let count = 1;
    questions.forEach((item, i) => {
      if (item) {
        questions[i].count = count;
        count += 1;
      }
    });

    for (let i = 0; i < count; i += 1) {
      questions.unshift(undefined); // hack required to force re-render of form items
    }

    setQuestions([...questions]);
  };

  const deleteQuestion = (i) => {
    const result = [];
    const deletedQuestion = questions.splice(i, 1);

    questions.forEach((question) => (question !== deletedQuestion ? result.push(question) : null));

    return setQuestions(result);
  };

  const addTask = () => {
    setTasks([
      ...tasks,
      {
        title: undefined,
        description: undefined,
        minDate: undefined,
        maxDate: undefined,
        question: undefined,
        questionDescription: undefined,
        questionValues: [],
        questionRequired: false,
        count: tasks.length > 0 && tasks[tasks.length - 1] ? tasks[tasks.length - 1].count + 1 : 1,
        taskIcon: undefined,
      },
    ]);
  };

  const deleteTask = (i) => {
    const result = [];
    const deletedTask = tasks.splice(i, 1);

    tasks.forEach((task) => (task !== deletedTask ? result.push(task) : null));

    return setTasks(result);
  };

  const updateTask = (i, e) => {
    e.persist();
    tasks[i].title = e.target.value;

    setTasks(tasks);
  };

  const updateTaskDescription = (i, e) => {
    e.persist();
    tasks[i].description = e.target.value;

    setTasks(tasks);
  };

  const updateTaskMinDate = (i, e) => {
    e.persist();
    tasks[i].minDate = e.target.value;

    setTasks(tasks);
  };

  const updateTaskMaxDate = (i, e) => {
    e.persist();
    tasks[i].maxDate = e.target.value;

    setTasks(tasks);
  };

  const updateTaskQuestion = (i, e) => {
    e.persist();
    tasks[i].question = e.target.value;

    setTasks(tasks);
  };

  const updateTaskQuestionDescription = (i, e) => {
    e.persist();
    tasks[i].questionDescription = e.target.value;

    setTasks(tasks);
  };

  const updateTaskQuestionOptions = (i, v) => {
    tasks[i].questionValues = v;

    setTasks(tasks);
  };

  const updateTaskIcon = (i, v) => {
    tasks[i].taskIcon = v;

    setTasks(tasks);
  };

  const updateTaskQuestionRequired = (i, v) => {
    tasks[i].questionRequired = v;

    setTasks(tasks);
  };

  const updateCropFactors = (i, v, type) => {
    if (type === "crop") {
      cropFactors[i].crop = v;
    } else {
      cropFactors[i].factor = v;
    }

    if (cropFactors[cropFactors.length - 1].crop) {
      cropFactors.push({
        crop: undefined,
        factor: undefined,
      });
    }

    setCropFactors(cropFactors);
  };

  const deleteCropFactor = (i) => {
    cropFactors.splice(i, 1);

    const count = cropFactors.length;
    for (let k = 0; k < count; k += 1) {
      cropFactors.unshift(undefined); // hack required to force re-render of form items
    }

    setCropFactors(cropFactors);
  };

  const complete = (v) => {
    setLoading(true);

    updateMethod({
      ...v,
      ...{
        id: id,
        questions: questions,
        tasks: tasks,
        image: imageURL,
        content: content,
        description: description,
        advantages: advantages,
        co2Storage: co2Storage,
        disadvantages: disadvantages,
        proceeds: proceeds,
        cropFactors: cropFactors,
        continuation: continuation,
        postSuggestion1: postSuggestion1,
        postSuggestion2: postSuggestion2,
        quantityQuestionPlaceholder: quantityQuestionPlaceholder,
        signal,
      },
    })
      .then(() => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateContent = (item, v) => {
    const content = v.replace(/height="\d*"/g, 'height="auto" style="max-width: 100%;"');

    if (item === "description") {
      return setDescription(content);
    }
    if (item === "advantages") {
      return setAdvantages(content);
    }
    if (item === "disadvantages") {
      return setDisadvantages(content);
    }
    if (item === "co2Storage") {
      return setCo2Storage(content);
    }
    if (item === "proceeds") {
      return setProceeds(content);
    }
  };

  const updatePostSuggestion = (e, suggestion) => {
    if (suggestion === "postSuggestion1") {
      setPostSuggestion1(e.target.value);
    } else {
      setPostSuggestion2(e.target.value);
    }
  };

  const updateQuantityQuestionPlaceholder = (e) => {
    setQuantityQuestionPlaceholder(e.target.value);
  };

  useEffect(() => {
    getAllApplications();
    getMethod();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline="Update a method"
        link="/programs/methods"
        tooltipText="Back to methods"
        description={
          <>
            Update a method here. A URL will be automatically generated from the title and cannot be changed once saved.{" "}
            <Link tracking={false} onClick={() => setShowDrawer(true)}>
              <b>
                Explanation of P = (P<sub>o</sub> + P<sub>a</sub> + P<sub>q</sub>) * C<sub>f</sub> * R<sub>f</sub>
              </b>
            </Link>
            <br />
            <br />
            <small>
              <b>WARNING:</b> adding/removing/moving many tasks, questions and crop factors will generate a volume of data that your browser cannot handle and
              it will likely crash. Be prepared and cautious. If many changes are made, please save and return for more.
            </small>
          </>
        }
      />
      <Row className="midi-form">
        <Col>
          <Form name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
            <Row>
              <Col>
                <Tabs
                  size="small"
                  items={[
                    {
                      label: "General",
                      key: "general",
                      children: (
                        <ErrorBoundary>
                          <MethodGeneralTab
                            applications={applications}
                            imageSuccess={imageSuccess}
                            imageURL={imageURL}
                            method={method}
                            setImageSuccess={setImageSuccess}
                            setImageURL={setImageURL}
                            setLoading={setLoading}
                            t={t}
                            updateContent={updateContent}
                            updatePostSuggestion={updatePostSuggestion}
                          />
                        </ErrorBoundary>
                      ),
                    },
                    {
                      label: "Recommendations",
                      key: "recommendations",
                      children: (
                        <ErrorBoundary>
                          <MethodRecommendationsTab method={method} t={t} />
                        </ErrorBoundary>
                      ),
                    },
                    {
                      label: "Po",
                      key: "po",
                      children: (
                        <ErrorBoundary>
                          <MethodPoTab
                            addQuestion={addQuestion}
                            cropFactors={cropFactors}
                            deleteCropFactor={deleteCropFactor}
                            deleteQuestion={deleteQuestion}
                            method={method}
                            moveQuestionDown={moveQuestionDown}
                            moveQuestionUp={moveQuestionUp}
                            questions={questions}
                            t={t}
                            updateCropFactors={updateCropFactors}
                            updatePlaceholder={updatePlaceholder}
                            updateQuestion={updateQuestion}
                            updateQuestionOptional={updateQuestionOptional}
                            updateQuestionOptions={updateQuestionOptions}
                            updateQuestionSf={updateQuestionSf}
                            updateQuestionType={updateQuestionType}
                          />
                        </ErrorBoundary>
                      ),
                    },
                    {
                      label: "Pa",
                      key: "pa",
                      children: (
                        <ErrorBoundary>
                          <MethodPaTab
                            addQuestion={addQuestion}
                            deleteQuestion={deleteQuestion}
                            method={method}
                            moveQuestionDown={moveQuestionDown}
                            moveQuestionUp={moveQuestionUp}
                            questions={questions}
                            sa={sa}
                            setSa={setSa}
                            updatePlaceholder={updatePlaceholder}
                            updateQuestion={updateQuestion}
                            updateQuestionOptional={updateQuestionOptional}
                            updateQuestionOptions={updateQuestionOptions}
                            updateQuestionSf={updateQuestionSf}
                            updateQuestionType={updateQuestionType}
                          />
                        </ErrorBoundary>
                      ),
                    },
                    {
                      label: "Pq",
                      key: "pq",
                      children: (
                        <ErrorBoundary>
                          <MethodPqTab
                            addQuestion={addQuestion}
                            deleteQuestion={deleteQuestion}
                            method={method}
                            moveQuestionDown={moveQuestionDown}
                            moveQuestionUp={moveQuestionUp}
                            questions={questions}
                            setSq={setSq}
                            sq={sq}
                            updatePlaceholder={updatePlaceholder}
                            updateQuantityQuestionPlaceholder={updateQuantityQuestionPlaceholder}
                            updateQuestion={updateQuestion}
                            updateQuestionOptional={updateQuestionOptional}
                            updateQuestionOptions={updateQuestionOptions}
                            updateQuestionSf={updateQuestionSf}
                            updateQuestionType={updateQuestionType}
                          />
                        </ErrorBoundary>
                      ),
                    },
                    {
                      label: "Tasks",
                      key: "tasks",
                      children: (
                        <ErrorBoundary>
                          <MethodTasksTab
                            addTask={addTask}
                            deleteTask={deleteTask}
                            tasks={tasks}
                            taskIcons={taskIcons}
                            updateTask={updateTask}
                            updateTaskDescription={updateTaskDescription}
                            updateTaskIcon={updateTaskIcon}
                            updateTaskMaxDate={updateTaskMaxDate}
                            updateTaskMinDate={updateTaskMinDate}
                            updateTaskQuestion={updateTaskQuestion}
                            updateTaskQuestionDescription={updateTaskQuestionDescription}
                            updateTaskQuestionOptions={updateTaskQuestionOptions}
                            updateTaskQuestionRequired={updateTaskQuestionRequired}
                          />
                        </ErrorBoundary>
                      ),
                    },
                    {
                      label: "Continuation",
                      key: "continuation",
                      children: (
                        <ErrorBoundary>
                          <MethodContinuationTab continuation={continuation} toggleContinuation={toggleContinuation} updateContinuation={updateContinuation} />
                        </ErrorBoundary>
                      ),
                    },
                  ]}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Item>
                  <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                    Save method
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Drawer visible={ShowDrawer} onClose={() => setShowDrawer(false)} title="Points calculations">
        <div className="content padding-bottom-200">
          <h3>
            P = (P<sub>o</sub> + P<sub>a</sub> + P<sub>q</sub>) * C<sub>f</sub> * R<sub>f</sub>
          </h3>
          <small>
            <b>P</b>: the total points received by the user when applying a method. The points are moved to the wallet on completion of the method.
            <br />
            <b>
              P<sub>o</sub>
            </b>
            : the points applied after answering onboarding questions
            <br />
            <b>
              P<sub>a</sub>
            </b>
            : the points applied after answering area questions
            <br />
            <b>
              P<sub>q</sub>
            </b>
            : the points applied after answering quantity questions
            <br />
            <b>
              C<sub>f</sub>
            </b>
            : the continuation factor applied to the equation, when a continuous method is continued after the first year (see continuation). For example, the
            user should receive less points after the first year.
            <br />
            <b>
              R<sub>f</sub>
            </b>
            : the reduction factor applied to the equation, based on the user's response when they continue a method (see continuation). For example, the user
            has reduced their use of the method in the second year.
          </small>

          <h3 className="margin-top-200">
            P<sub>o</sub> = S<sub>o</sub> * A * S<sub>f</sub> + F
          </h3>
          <small>
            <b>
              S<sub>o</sub>
            </b>
            : the sequestration factor applied to the equation for every user, every time (see P<sub>o</sub>).
            <br />
            <b>A</b>: the area of the field, as entered by the user.
            <br />
            <b>
              S<sub>f</sub>
            </b>
            : the sequestration factor applied to the equation based on the answers to the method onboarding questions (see P<sub>o</sub>). For multiple
            questions, this is multiplied (S<sub>f1</sub> * S<sub>f2</sub>...).
            <br />
            <b>F</b>: the crop factor added to the equation, based on the type of crop selected by the user for the current year (see P<sub>o</sub>
            ).
            <br />
          </small>

          <h3 className="margin-top-200">
            P<sub>a</sub> = S<sub>a</sub> * A<sub>u</sub> * S<sub>f</sub>
          </h3>
          <small>
            <b>
              S<sub>a</sub>
            </b>
            : the sequestration factor applied to the equation for every user, every time (see P<sub>a</sub>).
            <br />
            <b>
              A<sub>u</sub>
            </b>
            : the area of the method being applied, as entered by the user. The option for L * B is also given, and calculated as area.
            <br />
            <b>
              S<sub>f</sub>
            </b>
            : the sequestration factor applied to the equation based on the answers to the method area questions (see P<sub>a</sub>). For multiple questions,
            this is multiplied (S<sub>f1</sub> * S<sub>f2</sub>...).
            <br />
          </small>

          <h3 className="margin-top-200">
            P<sub>q</sub> = S<sub>q</sub> * Q * S<sub>f</sub> * A
          </h3>
          <small>
            <b>
              S<sub>a</sub>
            </b>
            : the sequestration factor applied to the equation for every user, every time (see P<sub>q</sub>).
            <br />
            <b>Q</b>: a quantity, as entered by the user.
            <br />
            <b>
              S<sub>f</sub>
            </b>
            : the sequestration factor applied to the equation based on the answers to the method area questions (see P<sub>q</sub>). For multiple questions,
            this is multiplied (S<sub>f1</sub> * S<sub>f2</sub>...).
            <br />
            <b>A</b>: the area of the field, as entered by the user.
            <br />
          </small>

          <h3 className="margin-top-200">Continuous methods</h3>
          <small>
            If the method is continuous;
            <ul>
              <li>the initial method and points will be applied in the same way as a once-off method.</li>
              <li>when the user completes the method, a new task will be created to verify the method again after X weeks.</li>
              <li>you must specify how many weeks after the first completion the user should complete the follow-on task.</li>
              <li>you must also specify a task title, description and question for the follow-on task.</li>
              <li>
                when completing the follow on task, the user will enter a percentage increase/decrease of their method (for example, they added to or removed
                some of the method)
              </li>
              <li>the question that you enter will be asked for this percentage (for example "Did you add/remove trees (specify a percent)?").</li>
              <li>
                the points that the method receives will then be multiplied by their percentage and the C<sub>f</sub> that you entered.
              </li>
              <li>this cycle will repeat every X weeks.</li>
            </ul>
          </small>
        </div>
      </Drawer>
    </MainPage>
  );
};
